import { apiFetch } from "./client";

/** D-044 S0 — Admin Center nav read-model. Admin-tier only; the backend guard
 *  (AdminCenterAccessGuard, NULL=DENY) is the SoT — the client only gates UI
 *  affordances via permissions.canViewAdminCenter. */

export interface AdminNavScreen {
  key: string;
  label: string;
  route: string;
  built: boolean;
  slice: string;
}

export interface AdminNavLinkOut {
  key: string;
  label: string;
  href: string;
  external: boolean;
}

export interface AdminCenterNavResponse {
  screens: AdminNavScreen[];
  linkOut: AdminNavLinkOut[];
}

export function fetchAdminCenterNav(signal?: AbortSignal): Promise<AdminCenterNavResponse> {
  return apiFetch<AdminCenterNavResponse>("/Custom/admin-center/nav", { signal });
}

// ── D-044 S1 — #1 Users list + #2 Add-user wizard ──────────────────────────
// Backend-owned (D-017): tier/functional = resolver-derived, counts, preview.

export interface AdminUserTeam {
  id: string;
  name: string;
  departmentName: string | null;
}

/** D-123 — кожна disabled/hidden login-block дія несе reasonCode + UI рендерить
 *  текст (0 мовчазних disabled). */
export type LoginActionReason =
  | "self_edit_blocked"
  | "no_capability"
  | "last_admin_protection"
  | "email_edit_deferred_d125";

export interface AdminUserAllowedAction {
  action: "login_edit" | "email_edit";
  enabled: boolean;
  reasonCode?: LoginActionReason;
}

export interface AdminUserRow {
  id: string;
  name: string;
  userName: string;
  email: string;
  isActive: boolean;
  type: string;
  /** resolver-derived (AC-ADM-002); null = no tier / conflict → DENY */
  tier: "manager" | "supervisor" | "director" | "admin_data_all" | null;
  tierLabel: string | null;
  tierConflict: boolean;
  /** D-541 AD-0: {key,label} — key = технічний role-key (стабільний), label = UA display */
  functional: { key: string; label: string }[];
  capabilities: { key: string; label: string }[];
  teams: AdminUserTeam[];
  noTeam: boolean;
  noTier: boolean;
  /** S2 MP4 — BACKEND-calc: функц-роль ≠ dept-membership */
  functionalMismatch?: boolean;
  lastLoginAt: string | null;
  /** D-123 login-block — усе backend-derived (UI не обчислює/не хардкодить) */
  loginMismatch: boolean;
  expectedGoogleEmail: string | null;
  managedClientCount: number;
  allowedActions: AdminUserAllowedAction[];
}

export interface AdminSystemUserRow {
  id: string;
  name: string;
  userName: string;
  isActive: boolean;
  note: string;
}

export interface AdminUsersOptions {
  tiers: { key: string; label: string; hint: string }[];
  functional: { key: string; label: string }[];
  capabilities: { key: string; label: string }[];
  teams: AdminUserTeam[];
}

export interface AdminUsersResponse {
  users: AdminUserRow[];
  systemUsers: AdminSystemUserRow[];
  counts: { total: number; active: number; inactive: number; noTeam: number };
  options: AdminUsersOptions;
  /** D-123 — корп-домени логіну (backend config; UI лише рендерить, не хардкодить) */
  allowedDomains: string[];
}

export interface SetUserLoginResult {
  status: string;
  userName: string;
  sessionRevoke: "ok" | "failed";
  /** non-null коли revoke впав — НЕ тихий success (TZ §6) */
  warning: string | null;
}

export interface AccessPreviewRow {
  key: string;
  label: string;
}

export interface AccessPreviewResponse {
  tier: string | null;
  tierLabel: string | null;
  scopeMode: "own" | "team" | "all" | "deny";
  clientCount: number;
  countExact: boolean;
  capabilities: string[];
  see: AccessPreviewRow[];
  notSee: AccessPreviewRow[];
}

export interface RoleConfig {
  tier: string | null;
  functional: string | null;
  capabilities: string[];
  teamId?: string | null;
  adminConfirmed?: boolean;
}

export function fetchAdminUsers(signal?: AbortSignal): Promise<AdminUsersResponse> {
  return apiFetch<AdminUsersResponse>("/Custom/admin-center/users", { signal });
}

export function fetchAccessPreview(cfg: RoleConfig, signal?: AbortSignal): Promise<AccessPreviewResponse> {
  return apiFetch<AccessPreviewResponse>("/Custom/admin-center/access-preview", {
    method: "POST",
    body: {
      tier: cfg.tier,
      functional: cfg.functional,
      capabilities: cfg.capabilities,
      teamId: cfg.teamId ?? null,
    },
    signal,
  });
}

export interface CreateUserPayload extends RoleConfig {
  name: string;
  email: string;
  phone?: string;
}

export function createAdminUser(payload: CreateUserPayload): Promise<{ id: string; status: string }> {
  return apiFetch("/Custom/admin-center/users", { method: "POST", body: payload });
}

export function setUserRoles(userId: string, cfg: RoleConfig): Promise<{ status: string }> {
  return apiFetch(`/Custom/admin-center/users/${encodeURIComponent(userId)}/roles`, {
    method: "POST",
    body: { tier: cfg.tier, functional: cfg.functional, capabilities: cfg.capabilities },
  });
}

export function setUserTeam(userId: string, teamId: string | null): Promise<{ status: string }> {
  return apiFetch(`/Custom/admin-center/users/${encodeURIComponent(userId)}/team`, {
    method: "POST",
    body: { teamId },
  });
}

/** D-123 — змінити логін (userName). Backend нормалізує+валідує+revoke-сесій. */
export function setUserLogin(userId: string, userName: string): Promise<SetUserLoginResult> {
  return apiFetch(`/Custom/admin-center/users/${encodeURIComponent(userId)}/login`, {
    method: "POST",
    body: { userName },
  });
}

export function setUserActive(userId: string, active: boolean): Promise<{ status: string }> {
  return apiFetch(
    `/Custom/admin-center/users/${encodeURIComponent(userId)}/${active ? "reactivate" : "deactivate"}`,
    { method: "POST", body: {} },
  );
}

// ── D-044 S2 — #3 Org-tree + #4 Roles matrix ────────────────────────────────

export interface OrgMember {
  id: string;
  name: string;
  isActive: boolean;
}

export interface OrgTeam {
  id: string;
  name: string;
  departmentId: string | null;
  supervisorId: string | null;
  supervisorName: string | null;
  supervisorActive: boolean | null;
  members: OrgMember[];
}

export interface OrgDepartment {
  id: string;
  name: string;
  code: string;
  active: boolean;
  /** MP1: sales_scope = client-visibility · functional_inbox = request-routing */
  orgType: "sales_scope" | "functional_inbox";
  teams: OrgTeam[];
}

export interface OrgTreeResponse {
  departments: OrgDepartment[];
  unassignedTeams: OrgTeam[];
  userOptions: { id: string; name: string }[];
  allowedActions: Record<string, boolean>;
}

export function fetchOrgTree(signal?: AbortSignal): Promise<OrgTreeResponse> {
  return apiFetch<OrgTreeResponse>("/Custom/admin-center/org/tree", { signal });
}

export const orgMutations = {
  createDepartment: (body: { name: string; orgType: string; code?: string }) =>
    apiFetch<{ id: string }>("/Custom/admin-center/org/departments", { method: "POST", body }),
  createTeam: (body: { name: string; departmentId?: string | null }) =>
    apiFetch<{ id: string }>("/Custom/admin-center/org/teams", { method: "POST", body }),
  renameTeam: (body: { teamId: string; name: string }) =>
    apiFetch("/Custom/admin-center/org/teams/rename", { method: "POST", body }),
  moveTeam: (body: { teamId: string; departmentId: string | null }) =>
    apiFetch("/Custom/admin-center/org/teams/move", { method: "POST", body }),
  setSupervisor: (body: { teamId: string; userId: string | null }) =>
    apiFetch("/Custom/admin-center/org/teams/supervisor", { method: "POST", body }),
  addMember: (body: { teamId: string; userId: string }) =>
    apiFetch("/Custom/admin-center/org/members/add", { method: "POST", body }),
  moveMember: (body: { userId: string; fromTeamId: string; toTeamId: string }) =>
    apiFetch("/Custom/admin-center/org/members/move", { method: "POST", body }),
};

export interface RoleCatalogHolder {
  id: string;
  name: string;
}

export interface RoleCatalogRow {
  key: string;
  label: string;
  hint: string;
  holders: RoleCatalogHolder[];
  holdersCount: number;
}

export interface RoleCatalogGroup {
  key: string;
  title: string;
  readOnly: boolean;
  warning?: string;
  note?: string;
  rows: RoleCatalogRow[];
}

export interface RolesCatalogResponse {
  groups: RoleCatalogGroup[];
  scopeLockedNote: string;
}

export function fetchRolesCatalog(signal?: AbortSignal): Promise<RolesCatalogResponse> {
  return apiFetch<RolesCatalogResponse>("/Custom/admin-center/roles/catalog", { signal });
}

// ── D-044 S3 — #6 Departments & routes (absorb IR Admin drawer, D-040) ──────
// Backend-owned (D-017): severity/kind/aggregate/allowedActions = сервер.

export interface RoutesResponsible {
  id: string;
  name: string;
  isActive: boolean;
  /** P7: admin-tier = pilot seed (Igor) */
  isSeed: boolean;
}

export interface RoutesDepartment {
  id: string;
  name: string;
  code: string;
  active: boolean;
  orgType: "sales_scope" | "functional_inbox";
  headId: string | null;
  headName: string | null;
  headIsSeed: boolean;
  claimSlaDefaultHours: number | null;
  completeSlaDefaultHours: number | null;
  notifyInApp: boolean;
  notifyEmail: boolean;
  responsibles: RoutesResponsible[];
  routeCount: number;
}

export type RouteSeverity = "ok" | "warning" | "error";

export interface AdminRouteRow {
  id: string;
  requestType: string;
  label: string;
  departmentCode: string;
  departmentId: string | null;
  departmentName: string | null;
  createable: boolean;
  enabled: boolean;
  /** 'request' (#9) | 'actionEngine' (read-only, AC-052) */
  kind: "request" | "actionEngine";
  severity: RouteSeverity;
  severityReason: string | null;
  responsibles: RoutesResponsible[];
  claimSlaHours: number | null;
  completeSlaHours: number | null;
  defaultPriority: "Normal" | "High";
  allowedRequesterRoles: string[];
  /** MP3: drawer-only, НЕ основна колонка P0 */
  defaultAssigneeId: string | null;
  defaultAssigneeName: string | null;
}

export interface RoutesConfigResponse {
  departments: RoutesDepartment[];
  routes: AdminRouteRow[];
  /** живить #7 Health (BG-2) */
  aggregate: { noResponsible: number; seedAdmin: number; total: number };
  userOptions: { id: string; name: string; isSeed: boolean }[];
  allowedActions: Record<string, boolean>;
}

export function fetchRoutesConfig(signal?: AbortSignal): Promise<RoutesConfigResponse> {
  return apiFetch<RoutesConfigResponse>("/Custom/admin-center/routes/config", { signal });
}

export const routesMutations = {
  toggleCreateable: (body: { routeId: string; enable: boolean }) =>
    apiFetch<{ status: string; createable: boolean }>("/Custom/admin-center/routes/toggle-createable", {
      method: "POST",
      body,
    }),
  assignResponsible: (body: { departmentId: string; userId: string; replaceUserId?: string | null }) =>
    apiFetch<{ status: string }>("/Custom/admin-center/routes/assign-responsible", { method: "POST", body }),
  setHead: (body: { departmentId: string; userId: string | null }) =>
    apiFetch<{ status: string }>("/Custom/admin-center/routes/set-head", { method: "POST", body }),
  saveDepartment: (body: {
    id?: string;
    name: string;
    code: string;
    active: boolean;
    headId?: string | null;
    responsibleIds?: string[];
    claimSlaDefaultHours?: number | null;
    completeSlaDefaultHours?: number | null;
    notifyInApp?: boolean;
    notifyEmail?: boolean;
  }) => apiFetch<{ status: string; id: string }>("/Custom/admin-center/routes/department-save", { method: "POST", body }),
  saveRoute: (body: {
    id?: string;
    requestType: string;
    label: string;
    department: string;
    enabled: boolean;
    claimSlaHours?: number | null;
    completeSlaHours?: number | null;
    defaultPriority?: "Normal" | "High";
  }) => apiFetch<{ status: string; id: string }>("/Custom/admin-center/routes/route-save", { method: "POST", body }),
  // D-044h — soft-deactivate/reactivate відділу (backend-enforced manager exclusion)
  setDepartmentActive: (body: { departmentId: string; active: boolean }) =>
    apiFetch<{ status: string; active: boolean; routeCount: number }>(
      "/Custom/admin-center/routes/department-active",
      { method: "POST", body },
    ),
};

// ── D-044 S4 — #7 System Health (P3 cached aggregator) ──────────────────────

export type HealthSeverity = "ok" | "warning" | "error";

export interface HealthSignal {
  key: string;
  label: string;
  severity: HealthSeverity;
  value: string;
  /** AC-061: джерело-запит (tooltip) */
  source: string;
  /** §0: кожен не-ok → actionable link; MP5 лише лінк клікабельний */
  link: { label: string; to?: string; href?: string; external?: boolean } | null;
  warnNote: string | null;
}

export interface HealthSnapshot {
  signals: HealthSignal[];
  summary: { ok: number; warning: number; error: number; total: number };
  lastComputedAt: string;
}

export interface HealthResponse {
  snapshot: HealthSnapshot;
  stale: boolean;
  computeError: boolean;
}

export function fetchAdminHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return apiFetch<HealthResponse>("/Custom/admin-center/health", { signal });
}

export function refreshAdminHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>("/Custom/admin-center/health/refresh", { method: "POST", body: {} });
}

// ── D-044 S4 — #8 Audit Log (read CSecurityAudit, P8) ───────────────────────

export interface AuditObjectLink {
  state: "accessible" | "deleted" | "sensitive" | "none";
  name: string | null;
  route?: string;
  reason?: string;
}

export interface AuditRow {
  id: string;
  occurredAt: string;
  actor: { id: string | null; name: string };
  eventType: string;
  action: string;
  objectType: string;
  objectId: string;
  sourceScreen: string | null;
  severity: string;
  /** MP8: server-redacted JSON (секрети → •••) */
  oldValue: string | null;
  newValue: string | null;
  objectLink: AuditObjectLink;
}

export interface AuditFilters {
  actorId?: string;
  action?: string;
  sourceScreen?: string;
  days?: number;
  q?: string;
}

export interface AuditResponse {
  items: AuditRow[];
  count: number;
  limit: number;
  windowDays: number;
  options: {
    actors: { id: string; name: string }[];
    actions: string[];
    screens: string[];
  };
}

// ── D-044 S5 — #5 Access preview / view-as (SECURITY-critical) ──────────────

export interface ViewAsTarget {
  id: string;
  name: string;
  tierLabel: string | null;
  eligible: boolean;
  /** server-enforced причина (MP4): admin / super / self / system / inactive */
  reason: string | null;
}

export interface ViewAsPreviewResponse extends AccessPreviewResponse {
  computedAt: string;
  eligible: boolean;
  ineligibleReason: string | null;
}

export interface ViewAsStartResponse {
  sessionId: string;
  /** видається ОДИН раз */
  secret: string;
  expiresAt: string;
  target: { name: string };
}

export interface ViewAsStatusResponse {
  active: boolean;
  expiresAt: string;
  endReason: string | null;
  target: { name: string };
}

export function fetchViewAsTargets(signal?: AbortSignal): Promise<{ targets: ViewAsTarget[] }> {
  return apiFetch("/Custom/admin-center/view-as/targets", { signal });
}

export function fetchViewAsPreview(targetUserId: string, signal?: AbortSignal): Promise<ViewAsPreviewResponse> {
  return apiFetch("/Custom/admin-center/view-as/preview", { method: "POST", body: { targetUserId }, signal });
}

export function startViewAs(targetUserId: string): Promise<ViewAsStartResponse> {
  return apiFetch("/Custom/admin-center/view-as/start", { method: "POST", body: { targetUserId } });
}

export function exitViewAs(sessionId: string, secret: string): Promise<{ status: string; endReason: string }> {
  return apiFetch("/Custom/admin-center/view-as/exit", { method: "POST", body: { sessionId, secret } });
}

/** Best-effort end на закриття вкладки (keepalive переживає unload). Гарантія = cron 15хв. */
export function endViewAsBeacon(sessionId: string, secret: string): void {
  void apiFetch("/Custom/admin-center/view-as/exit", {
    method: "POST",
    body: { sessionId, secret },
    keepalive: true,
  }).catch(() => undefined);
}

export function fetchViewAsStatus(sessionId: string, secret: string, signal?: AbortSignal): Promise<ViewAsStatusResponse> {
  return apiFetch("/Custom/admin-center/view-as/status", { query: { sessionId, secret }, signal });
}

export function fetchAuditLog(filters: AuditFilters, signal?: AbortSignal): Promise<AuditResponse> {
  const params = new URLSearchParams();
  if (filters.actorId) params.set("actorId", filters.actorId);
  if (filters.action) params.set("action", filters.action);
  if (filters.sourceScreen) params.set("sourceScreen", filters.sourceScreen);
  if (filters.days) params.set("days", String(filters.days));
  if (filters.q) params.set("q", filters.q);
  const qs = params.toString();
  return apiFetch<AuditResponse>(`/Custom/admin-center/audit${qs ? `?${qs}` : ""}`, { signal });
}

// ── G2-S3 — Юрособи Kormax (KormaxLegalEntity governance, D2=C) ──────────────
// Admin-only (AdminCenterAccessGuard). List carries computed linkedContracts +
// lastUsed + allowed teams/users + seed-dedup candidates; mutations gated by the
// S1 AvailabilityGuard hook (status validity + never-zero-default).

export interface AdminLeRef {
  id: string;
  name: string;
}

export interface AdminLegalEntityRow {
  id: string;
  name: string;
  legalName: string;
  edrpou: string | null;
  status: "active" | "inactive" | "historical";
  defaultForAll: boolean;
  availableForNewContracts: boolean;
  allowedTeams: AdminLeRef[];
  allowedUsers: AdminLeRef[];
  allowedTeamsIds: string[];
  allowedUsersIds: string[];
  aliases: string | null;
  validFrom: string | null;
  validTo: string | null;
  linkedContracts: number;
  lastUsed: string | null;
}

export interface AdminLegalEntityDupCandidate {
  normalized: string;
  members: AdminLeRef[];
}

export interface AdminLegalEntitiesResponse {
  items: AdminLegalEntityRow[];
  options: { statuses: string[]; teams: AdminLeRef[]; users: AdminLeRef[] };
  dupCandidates: AdminLegalEntityDupCandidate[];
}

export interface AdminLegalEntityPayload {
  name?: string;
  legalName?: string;
  edrpou?: string | null;
  status?: string;
  defaultForAll?: boolean;
  availableForNewContracts?: boolean;
  allowedTeamsIds?: string[];
  allowedUsersIds?: string[];
  aliases?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
}

export function fetchAdminLegalEntities(signal?: AbortSignal): Promise<AdminLegalEntitiesResponse> {
  return apiFetch<AdminLegalEntitiesResponse>("/Custom/admin-center/legal-entities", { signal });
}

export function createAdminLegalEntity(
  body: AdminLegalEntityPayload,
): Promise<{ id: string; status: string }> {
  return apiFetch("/Custom/admin-center/legal-entities", { method: "POST", body });
}

export function updateAdminLegalEntity(
  id: string,
  body: AdminLegalEntityPayload,
): Promise<{ id: string; status: string }> {
  return apiFetch(`/Custom/admin-center/legal-entities/${encodeURIComponent(id)}`, {
    method: "POST",
    body,
  });
}
